import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Chip, Divider, CircularProgress, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { createExplorerTheme } from '../../theme/theme';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import axios from 'axios';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// Styled component for each data item
const DataItem = styled(Paper)(({ theme }) => ({
    // padding: theme.spacing(2),
    // textAlign: 'left',
    // color: theme.palette.text.primary,
    // backgroundColor: theme.palette.background.paper,
    // border: '1px solid',
    // borderColor: theme.palette.divider,
    // borderRadius: '12px',
    // height: '100%',
    // display: 'flex',
    // flexDirection: 'column',
    // justifyContent: 'center',
    // transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
    // '&:hover': {
    //     boxShadow: theme.shadows[4],
    //     transform: 'translateY(-2px)',
    // },
}));

// Main component to display account details
const AccountDetails = ({ data, loading }) => {
    const { t } = useTranslation();
    const { theme: baseTheme } = useTheme();
    const theme = createExplorerTheme(baseTheme);
    const { price: btcPrice } = useBitcoinPrice();

    // Helper function to format large numbers with commas
    const formatNumber = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return num;
        return num.toLocaleString('en-US', { maximumFractionDigits: 8 });
    };

    // Helper function to format the ISO date string
    const formatUpdatedAt = (isoString) => {
        if (!isoString) return 'N/A';
        return new Date(isoString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        });
    };

    const renderDetailItem = (icon, label, value, isMono = false, isChip = false) => (
        <Box >
            <DataItem variant="outlined">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {loading ? (
                        <Skeleton variant="circular" width={24} height={24} />
                    ) : (
                        icon
                    )}
                    <Typography variant="caption" sx={{ ml: 1, textTransform: 'uppercase' }}>
                        {loading ? <Skeleton width={80} /> : label}
                    </Typography>
                </Box>
                {loading ? (
                    isChip ? (
                        <Skeleton variant="rounded" width={80} height={24} />
                    ) : (
                        <Skeleton variant="text" width="90%" height={28} />
                    )
                ) : (
                    isChip ? (
                        <Chip
                            label={value || t('explorer.notSet')}
                            size="small"
                            color={value ? "info" : "default"}
                            sx={{ width: 'fit-content' }}
                        />
                    ) : (
                        <Typography
                            variant="body1"
                            component="div"
                            sx={{
                                fontWeight: '500',
                                wordBreak: 'break-all',
                                fontFamily: isMono ? 'monospace' : 'inherit',
                                fontSize: isMono ? '0.9rem' : '1.1rem'
                            }}
                        >
                            {value || t('common.notAvailable')}
                        </Typography>
                    )
                )}
            </DataItem>
        </Box>
    );

    return(
        <Box sx={{
            width:"100%",
        }}>
            {renderDetailItem(<AccountBalanceWalletIcon color="secondary" />, t('explorer.balance'),
                data?.balance ? (
                    <Box>
                        <Typography variant="body1" sx={{ fontWeight: '500', fontSize: '1.1rem' }}>
                            {`${ formatNumber(parseInt(data.balance, 10) / 1e8) } BTC`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                            ${formatNumber((parseInt(data.balance, 10) / 1e8) * btcPrice)}
                        </Typography>
                    </Box>
                ) : null
            )}
        </Box>
    )
};



// The main App component that fetches data and renders AccountDetails
export default function OdinCanisterBalance() {
    const { theme: baseTheme } = useTheme();
    const theme = createExplorerTheme(baseTheme);

    const [accountData, setAccountData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('https://icrc-api.internetcomputer.org/api/v1/ledgers/mxzaz-hqaaa-aaaar-qaada-cai/accounts/ztwhb-qiaaa-aaaaj-azw7a-cai');

                // The API returns the account data inside an array
                if (response.data.id) {
                    setAccountData(response.data);
                } else {
                    throw new Error('Account data not found in API response.');
                }
            } catch (e) {
                if (e.response) {
                    // The request was made and the server responded with a status code
                    setError(`HTTP error! status: ${ e.response.status }`);
                } else if (e.request) {
                    // The request was made but no response was received
                    setError('Network error: No response received');
                } else {
                    // Something happened in setting up the request
                    setError(e.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []); // Empty dependency array means this effect runs once on mount

    if (loading) {
        return <AccountDetails data={null} loading={true} />;
    }

    if (error) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                // height: '100vh',
                bgcolor: theme.palette.background.default
            }}>
                <Typography color="error">{t('common.error')}: {error}</Typography>
            </Box>
        );
    }

    return accountData ? <AccountDetails data={accountData} loading={false} /> : null;
}
